import { observable, action, makeObservable } from 'mobx';

import agent from '../agent';

class OvertimeBankStore {
    constructor() {
        makeObservable(this, {
            currentMemberOvertimeBankList: observable,
            currentMemberList: observable,
            loading: observable,
            requestParams: observable,
            lastListLoadTime: observable,
            filters: observable,
            appliedFilters: observable,

            setFilter: action,
            onFilter: action,
            resetLastListLoadTime: action,
            loadEmployees: action,
            loadEmployeeOvertimeTotals: action,
            loadOvertimeTransactions: action,
            loadOvertimeTransaction: action,
            loadOvertimeTransactionsForDateAndType: action,
            updatePaidOvertimeBalance: action,
        });
    }

    currentMemberOvertimeBankList = {};
    currentMemberList = {};
    loading = false;
    lastListLoadTime = null;
    requestParams = null;

    filters = {
        currentUserOvertimeBankList: '',
        year: new Date().getFullYear(),
        fromDate: null,
        toDate: null,
        status: '',
        overtime_type: '',
    };
    appliedFilters = {
        currentUserOvertimeBankList: '',
        year: new Date().getFullYear(),
        fromDate: null,
        toDate: null,
        status: '',
        overtime_type: '',
    };

    setFilter(name, value) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
    }

    resetLastListLoadTime(value) {
        this.lastListLoadTime = value;
    }

    loadEmployees(params) {
        return agent.OvertimeBank.loadEmployees(params)
            .then(
                action(res => {
                    const employees = (res.employees || []).map(employee => ({
                        ...employee,
                        name: `${employee.first_name} ${employee.last_name}`
                    }));
                    this.currentMemberList = employees;
                    return employees;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    // Never touches currentMemberList - callers refreshing another employee's totals after a
    // year change (see OvertimeBank.js's handleYearChange) must update their own local state with
    // the result instead, so currentMemberList (used by AddPaidOvertimeModal for eligibility/
    // preview calculations, which are always current-year) is never overwritten with another
    // year's numbers.
    loadEmployeeOvertimeTotals(year, userId) {
        const params = {};
        if (year) params.year = year;
        if (userId) params.user_id = userId;
        return agent.OvertimeBank.loadEmployeeOvertimeTotals(params)
            .then(
                action(res => res)
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updatePaidOvertimeBalance(data) {
        return agent.OvertimeBank.updatePaidOvertimeBalance(data)
            .then(
                action(res => res)
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadOvertimeTransaction(id) {
        return agent.OvertimeBank.loadOvertimeTransaction(id)
            .then(
                action(res => res.transaction)
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadOvertimeTransactionsForDateAndType(params) {
        return agent.OvertimeBank.loadOvertimeTransactionsForDateAndType(params)
            .then(
                action(res => ({ overtimeTransactions: res.overtimeTransactions, summary: res.summary }))
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    loadOvertimeTransactions(params = {}, cb) {
        const _filters = params.filters || this.filters;
        const limit = params.pagesize || 10;
        const apiParams = {
            user_id: _filters.currentUserOvertimeBankList || undefined,
            year: _filters.year || undefined,
            from_date: _filters.fromDate || undefined,
            to_date: _filters.toDate || undefined,
            transaction_type: _filters.status || undefined,
            overtime_type: _filters.overtime_type || undefined,
            page: (params.page || 0) + 1,
            limit,
        };

        if (!apiParams.user_id) {
            this.lastListLoadTime = new Date();
            return Promise.resolve({ rows: [], pages: 0, time: this.lastListLoadTime });
        }
        this.loading = true;
        return agent.OvertimeBank.loadOvertimeTransactions(apiParams)
            .then(
                action(res => {
                    this.requestParams = apiParams;
                    this.currentMemberOvertimeBankList = res.rows;
                    this.lastListLoadTime = new Date();
                    this.loading = false;
                    if (cb) cb();
                    return {
                        rows: res.rows,
                        pages: Math.ceil((res.total || 0) / (res.limit || limit)),
                        time: this.lastListLoadTime,
                    };
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }
}

const _OvertimeBankStore = new OvertimeBankStore();
export default _OvertimeBankStore;
